/**
 * Thinking Dialect Registry — single source of truth for per-dialect inference
 * config: which user-facing levels each dialect accepts and how a resolved
 * tuning becomes the request-param shape the provider expects.
 *
 * Adding a new thinking-API variant (next Anthropic release, new OpenAI shape,
 * Gemini change) is one update here:
 *  1. Append the dialect literal to `THINKING_DIALECTS`.
 *  2. Add a per-dialect spec to `THINKING_DIALECT_SPECS` and its off forms to
 *     `OFF_FORMS_BY_DIALECT`.
 *  3. Add a `case` to `encodeTuning` that returns the typed result.
 *
 * No catalog booleans, no per-caller conditionals, no scattered branches.
 */

import { z } from "zod";

import { DbEnum } from "../generated/prisma-enums";

// =============================================================================
// Display modes — Anthropic only (legacy + adaptive both use them)
// =============================================================================

export const ANTHROPIC_THINKING_DISPLAY_VALUES = ["summarized", "omitted"] as const;
export type AnthropicThinkingDisplay = (typeof ANTHROPIC_THINKING_DISPLAY_VALUES)[number];

// =============================================================================
// Per-dialect user-facing level vocabularies
// =============================================================================

/** Legacy Anthropic thinking (Claude 4.6 and earlier). */
export const ANTHROPIC_LEGACY_LEVELS = ["fast", "detailed", "extended"] as const;
export type AnthropicLegacyLevel = (typeof ANTHROPIC_LEGACY_LEVELS)[number];

/** Adaptive Anthropic thinking (Claude 4.7+). */
export const ANTHROPIC_ADAPTIVE_LEVELS = ["low", "medium", "high", "xhigh", "max"] as const;
export type AnthropicAdaptiveLevel = (typeof ANTHROPIC_ADAPTIVE_LEVELS)[number];

/**
 * Every level a caller may store in `modelConfig.reasoningLevel` for an Anthropic
 * model, across both Anthropic dialects. `resolveModelTuning` maps a legacy value onto an
 * adaptive model through its rung, so a config is not bound to the dialect its
 * model happens to carry today — which is why the two vocabularies are one
 * accepted set at the schema and split only when a tuning is resolved.
 *
 * 🚨 **A SINGLE `z.enum` OVER THIS, NEVER `z.union([z.enum(A), z.enum(B)])` —
 * THE TWO INFER THE SAME TYPE AND ARE NOT INTERCHANGEABLE.** The CLI's
 * `gen:contract-help` derives each flag's `--choices()` by reading the v1
 * contract, and it can read a `z.enum`; a union of two defeats it and the
 * descriptor comes out `type: "unknown"` with NO `enumValues` at all. That is
 * silent in both gates — `contract-help.test.ts` only requires a flag or a
 * `bodyOnly` reason for a field that HAS `enumValues`, so a field that lost
 * them leaves the population instead of failing. Measured while widening the
 * v1 agent contract (NEX-3869): the union spelling replaced three offered
 * values with none.
 *
 * Deduped by filter rather than by `Set` for the same reason `GEMINI_EFFORT_LEVELS`
 * is: a `Set` erases the `[T, ...T[]]` tuple type `z.enum` requires. The two
 * vocabularies are disjoint today, so the filter removes nothing — it is what
 * keeps a duplicate `--choices()` entry impossible the day one of them grows a
 * name the other already has.
 */
export const ANTHROPIC_EFFORT_LEVELS = [
  ...ANTHROPIC_LEGACY_LEVELS,
  ...ANTHROPIC_ADAPTIVE_LEVELS.filter(
    (lvl): lvl is Exclude<AnthropicAdaptiveLevel, AnthropicLegacyLevel> =>
      !(ANTHROPIC_LEGACY_LEVELS as readonly string[]).includes(lvl)
  )
] as const;
export type AnthropicEffortLevel = (typeof ANTHROPIC_EFFORT_LEVELS)[number];

/** OpenAI reasoning effort (o-series, gpt-5*). "max" is offered on select models only — see `supportedReasoningLevels` in models.json. */
export const OPENAI_EFFORT_LEVELS = ["minimal", "low", "medium", "high", "xhigh", "max"] as const;
export type OpenAIEffortLevel = (typeof OPENAI_EFFORT_LEVELS)[number];

/** Google Gemini legacy thinkingBudget API (numeric). "dynamic" → -1. */
export const GEMINI_BUDGET_LEVELS = ["dynamic", "low", "medium", "high"] as const;
export type GeminiBudgetLevel = (typeof GEMINI_BUDGET_LEVELS)[number];

/** Google Gemini modern thinkingLevel API (enum). */
export const GEMINI_LEVEL_LEVELS = ["minimal", "low", "medium", "high"] as const;
export type GeminiLevelLevel = (typeof GEMINI_LEVEL_LEVELS)[number];

/**
 * Kimi reasoning effort (kimi-k3). Moonshot accepts only these three — it has no
 * "medium" and no "xhigh", so the vocabulary cannot be folded into the OpenAI one
 * despite the overlap on low/high/max.
 */
export const KIMI_EFFORT_LEVELS = ["low", "high", "max"] as const;
export type KimiEffortLevel = (typeof KIMI_EFFORT_LEVELS)[number];

/**
 * Union of every level value any dialect accepts. Kimi's three values all appear
 * in the arrays above today, so spreading them changes nothing now — it is what
 * keeps this total the day Moonshot adds a level none of the others has.
 */
export const ALL_DIALECT_EFFORT_LEVELS = [
  ...ANTHROPIC_LEGACY_LEVELS,
  ...ANTHROPIC_ADAPTIVE_LEVELS,
  ...OPENAI_EFFORT_LEVELS,
  ...GEMINI_BUDGET_LEVELS,
  ...GEMINI_LEVEL_LEVELS,
  ...KIMI_EFFORT_LEVELS
] as const;
export type AnyDialectEffortLevel = (typeof ALL_DIALECT_EFFORT_LEVELS)[number];

export const EFFORT_LADDER = ["minimal", "low", "medium", "high", "xhigh", "max"] as const;
export type EffortRung = (typeof EFFORT_LADDER)[number];
export const EffortRungSchema = z.enum(EFFORT_LADDER);

type LegacyWord = Exclude<AnyDialectEffortLevel, EffortRung>;

export const LEGACY_WORD_RUNGS = {
  fast: "low",
  detailed: "medium",
  extended: "high",
  dynamic: "high"
} as const satisfies Record<LegacyWord, EffortRung>;

const isLegacyWord = (word: AnyDialectEffortLevel): word is LegacyWord => word in LEGACY_WORD_RUNGS;

export function rungOf(word: AnyDialectEffortLevel): EffortRung {
  return isLegacyWord(word) ? LEGACY_WORD_RUNGS[word] : word;
}

export const DEFAULT_EFFORTS = [...EFFORT_LADDER, "none", "vendor-default"] as const;
export type DefaultEffort = (typeof DEFAULT_EFFORTS)[number];
export const DefaultEffortSchema = z.enum(DEFAULT_EFFORTS);

export const EFFORT_OFF_FORMS = [
  "omit",
  "disabled",
  "between-tools",
  "effort-none",
  "minimal",
  "thinking-disabled"
] as const;
export type EffortOffForm = (typeof EFFORT_OFF_FORMS)[number];
export const EffortOffFormSchema = z.enum(EFFORT_OFF_FORMS);

export const TEMPERATURE_RULES = ["always", "when-off", "never"] as const;
export type TemperatureRule = (typeof TEMPERATURE_RULES)[number];
export const TemperatureRuleSchema = z.enum(TEMPERATURE_RULES);

// =============================================================================
// Master dialect enum
// =============================================================================

export const THINKING_DIALECTS = [
  "anthropic-legacy",
  "anthropic-adaptive",
  "openai-reasoning",
  "gemini-budget",
  "gemini-level",
  "kimi-effort"
] as const;
export type ThinkingDialect = (typeof THINKING_DIALECTS)[number];
export const ThinkingDialectSchema = z.enum(THINKING_DIALECTS);

/**
 * Safely parse an arbitrary text value (typically a DB `VARCHAR` column or an
 * external JSON blob) into a `ThinkingDialect`. Returns `null` for `null`
 * inputs OR unrecognized strings. The optional `onUnknown` callback fires
 * exactly once when the input is non-null but not in the registry, giving
 * boundary code a hook to warn-log or instrument without coupling this
 * pure parser to any concrete logger.
 */
export function parseThinkingDialect(
  raw: string | null,
  onUnknown?: (raw: string) => void
): ThinkingDialect | null {
  if (raw === null) return null;
  const parsed = ThinkingDialectSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  onUnknown?.(raw);
  return null;
}

export const OFF_FORMS_BY_DIALECT = {
  "anthropic-legacy": ["omit"],
  "anthropic-adaptive": ["omit", "disabled", "between-tools"],
  "openai-reasoning": ["effort-none"],
  "gemini-budget": [],
  "gemini-level": ["minimal"],
  "kimi-effort": ["thinking-disabled"]
} as const satisfies Record<ThinkingDialect, readonly EffortOffForm[]>;

export function offFormsOf(dialect: ThinkingDialect | null): readonly EffortOffForm[] {
  return dialect ? OFF_FORMS_BY_DIALECT[dialect] : [];
}

// =============================================================================
// Per-dialect spec (metadata only)
// =============================================================================

export interface ThinkingDialectSpec {
  readonly dialect: ThinkingDialect;
  readonly provider: DbEnum.ModelProvider;
  readonly levels: readonly string[];
}

export const THINKING_DIALECT_SPECS = {
  "anthropic-legacy": {
    dialect: "anthropic-legacy",
    provider: DbEnum.ModelProvider.ANTHROPIC,
    levels: ANTHROPIC_LEGACY_LEVELS
  },
  "anthropic-adaptive": {
    dialect: "anthropic-adaptive",
    provider: DbEnum.ModelProvider.ANTHROPIC,
    levels: ANTHROPIC_ADAPTIVE_LEVELS
  },
  "openai-reasoning": {
    dialect: "openai-reasoning",
    provider: DbEnum.ModelProvider.OPEN_AI,
    levels: OPENAI_EFFORT_LEVELS
  },
  "gemini-budget": {
    dialect: "gemini-budget",
    provider: DbEnum.ModelProvider.GOOGLE_AI,
    levels: GEMINI_BUDGET_LEVELS
  },
  "gemini-level": {
    dialect: "gemini-level",
    provider: DbEnum.ModelProvider.GOOGLE_AI,
    levels: GEMINI_LEVEL_LEVELS
  },
  "kimi-effort": {
    dialect: "kimi-effort",
    provider: DbEnum.ModelProvider.KIMI,
    levels: KIMI_EFFORT_LEVELS
  }
} as const satisfies Record<ThinkingDialect, ThinkingDialectSpec>;

// =============================================================================
// The Anthropic subset — a VIEW of the registry, never a second vocabulary
// =============================================================================

/**
 * The dialects an Anthropic-speaking endpoint may use.
 *
 * Derived from {@link THINKING_DIALECT_SPECS}' own `provider` field rather than from how
 * the literals happen to be spelled, so the spec table stays the single author. A dialect
 * whose spec names ANTHROPIC is in this type the moment it is added to the registry.
 */
export type AnthropicThinkingDialect = {
  [D in ThinkingDialect]: (typeof THINKING_DIALECT_SPECS)[D]["provider"] extends DbEnum.ModelProvider.ANTHROPIC
    ? D
    : never;
}[ThinkingDialect];

/**
 * The same set as a value, for the write boundaries that offer it as a choice — the
 * custom-model contract's `thinkingDialect`, and the picker rendering it.
 *
 * 🔴 **THE TUPLE IS WRITTEN OUT, AND `assertEveryAnthropicDialectIsListed` BELOW IS WHAT
 * KEEPS IT HONEST.** `z.enum` requires a non-empty tuple and `Array.prototype.filter`
 * erases that, so deriving this with a filter costs either a cast or the enum. The
 * assertion buys back exactly what the filter would have given: a dialect added to
 * {@link THINKING_DIALECTS} with an ANTHROPIC spec is a COMPILE ERROR here until it is
 * listed, rather than a value this list silently omits while still reading as derived.
 */
export const ANTHROPIC_THINKING_DIALECTS = [
  "anthropic-legacy",
  "anthropic-adaptive"
] as const satisfies readonly AnthropicThinkingDialect[];

/** `never` exactly when every Anthropic dialect in the registry is listed above. */
type UnlistedAnthropicDialect = Exclude<
  AnthropicThinkingDialect,
  (typeof ANTHROPIC_THINKING_DIALECTS)[number]
>;

/**
 * The exhaustiveness assertion, as a value so nothing can drop it as unused.
 *
 * Its annotation is `true` while every Anthropic dialect is listed, and becomes the
 * MISSING dialect's own literal otherwise — so the compiler's message names the dialect
 * that was forgotten rather than pointing at a line.
 */
export const ANTHROPIC_THINKING_DIALECTS_ARE_EXHAUSTIVE: [UnlistedAnthropicDialect] extends [never]
  ? true
  : UnlistedAnthropicDialect = true;

export const AnthropicThinkingDialectSchema = z.enum(ANTHROPIC_THINKING_DIALECTS);

// =============================================================================
// Internal lookup tables
// =============================================================================

/** Token budget per legacy Anthropic level (4.6 path only). */
const ANTHROPIC_LEGACY_BUDGET_TOKENS: Record<AnthropicLegacyLevel, number> = {
  fast: 2_048,
  detailed: 6_144,
  extended: 10_240
};

/** Numeric budgets for the legacy Gemini thinkingBudget API. -1 = "dynamic". */
const GEMINI_BUDGET_TOKENS: Record<GeminiBudgetLevel, number> = {
  dynamic: -1,
  low: 1_024,
  medium: 4_096,
  high: 16_384
};

// =============================================================================
// Convenience accessors
// =============================================================================

/** Returns the spec for a dialect, or undefined when no dialect is set. */
export function getDialectSpec(
  dialect: ThinkingDialect | null | undefined
): ThinkingDialectSpec | undefined {
  if (!dialect) return undefined;
  return THINKING_DIALECT_SPECS[dialect];
}

/** Returns the user-facing levels accepted by a dialect (or empty when absent). */
export function getDialectLevels(dialect: ThinkingDialect | null | undefined): readonly string[] {
  return getDialectSpec(dialect)?.levels ?? [];
}

/** Reading slice of a catalog entry sufficient for dialect-based dispatch. */
export interface DialectCapabilities {
  readonly thinkingDialect?: ThinkingDialect | null;
  readonly supportedReasoningLevels?: readonly AnyDialectEffortLevel[];
}

/**
 * Derives the user-facing reasoning/thinking level vocabulary applicable to a
 * catalog entry — the per-model `supportedReasoningLevels`, or an empty array
 * when no dialect is set.
 */
export function getReasoningValues(entry: DialectCapabilities): readonly AnyDialectEffortLevel[] {
  if (!entry.thinkingDialect) return [];
  // Total by design: the catalog refine guarantees a non-empty array for every
  // thinking-capable entry, so this `?? []` only fires for unvalidated inputs
  // (e.g. hand-built test entries that bypass Zod) — not a production path.
  return entry.supportedReasoningLevels ?? [];
}

// =============================================================================
// Encoders — a resolved tuning in, the params one dialect's request carries out
// =============================================================================

export type ResolvedEffort =
  | { readonly kind: "level"; readonly rung: AnyDialectEffortLevel }
  | { readonly kind: "off"; readonly form: EffortOffForm }
  | { readonly kind: "vendor-default" };

export interface ResolvedTuning {
  readonly effort: ResolvedEffort;
  readonly display?: AnthropicThinkingDisplay;
  readonly temperature?: number;
}

interface SamplingParams {
  readonly temperature?: number;
}

export interface AnthropicLegacyThinking {
  readonly type: "enabled";
  readonly budget_tokens: number;
  readonly display?: AnthropicThinkingDisplay;
}

export interface AnthropicLegacyTuningParams extends SamplingParams {
  readonly dialect: "anthropic-legacy";
  readonly thinking?: AnthropicLegacyThinking;
  readonly maxTokensFloor?: number;
}

export type AnthropicAdaptiveThinkingField =
  | { readonly type: "adaptive"; readonly display: AnthropicThinkingDisplay }
  | { readonly type: "disabled" }
  | { readonly type: "between_tools" };

export interface AnthropicAdaptiveTuningParams extends SamplingParams {
  readonly dialect: "anthropic-adaptive";
  readonly thinking?: AnthropicAdaptiveThinkingField;
  readonly outputConfig?: { readonly effort: AnthropicAdaptiveLevel };
}

export interface OpenAIReasoningTuningParams extends SamplingParams {
  readonly dialect: "openai-reasoning";
  readonly reasoningEffort?: OpenAIEffortLevel | "none";
}

export interface GeminiBudgetTuningParams extends SamplingParams {
  readonly dialect: "gemini-budget";
  readonly thinkingConfig?: { readonly thinkingBudget: number };
}

export type GeminiWireLevel = Uppercase<GeminiLevelLevel>;

export interface GeminiLevelTuningParams extends SamplingParams {
  readonly dialect: "gemini-level";
  readonly thinkingConfig?: {
    readonly thinkingLevel: GeminiWireLevel;
    readonly includeThoughts: boolean;
  };
}

export interface KimiEffortTuningParams extends SamplingParams {
  readonly dialect: "kimi-effort";
  readonly reasoningEffort?: KimiEffortLevel;
  readonly thinking?: { readonly type: "disabled" };
}

export interface NoDialectTuningParams extends SamplingParams {
  readonly dialect: null;
}

export type TuningParams =
  | AnthropicLegacyTuningParams
  | AnthropicAdaptiveTuningParams
  | OpenAIReasoningTuningParams
  | GeminiBudgetTuningParams
  | GeminiLevelTuningParams
  | KimiEffortTuningParams
  | NoDialectTuningParams;

export function encodeTuning(
  dialect: ThinkingDialect | null,
  tuning: ResolvedTuning
): TuningParams {
  switch (dialect) {
    case null:
      return encodeNoDialectTuning(tuning);
    case "anthropic-legacy":
      return encodeAnthropicLegacyTuning(tuning);
    case "anthropic-adaptive":
      return encodeAnthropicAdaptiveTuning(tuning);
    case "openai-reasoning":
      return encodeOpenAIReasoningTuning(tuning);
    case "gemini-budget":
      return encodeGeminiBudgetTuning(tuning);
    case "gemini-level":
      return encodeGeminiLevelTuning(tuning);
    case "kimi-effort":
      return encodeKimiEffortTuning(tuning);
    default: {
      const unhandled: never = dialect;
      return unhandled;
    }
  }
}

function samplingOf(tuning: ResolvedTuning): SamplingParams {
  return tuning.temperature === undefined ? {} : { temperature: tuning.temperature };
}

function wordIn<Word extends string>(
  vocabulary: readonly Word[],
  rung: string,
  dialect: ThinkingDialect
): Word {
  const word = vocabulary.find((candidate) => candidate === rung);
  if (word === undefined) {
    throw new Error(`the ${dialect} dialect has no level "${rung}"`);
  }
  return word;
}

function offFormIn<Form extends EffortOffForm>(
  forms: readonly Form[],
  form: EffortOffForm,
  dialect: ThinkingDialect
): Form {
  const owned = forms.find((candidate) => candidate === form);
  if (owned === undefined) {
    throw new Error(`the ${dialect} dialect has no off form "${form}"`);
  }
  return owned;
}

export function encodeNoDialectTuning(tuning: ResolvedTuning): NoDialectTuningParams {
  if (tuning.effort.kind !== "vendor-default") {
    throw new Error("a model with no thinking dialect cannot be asked for an effort");
  }
  return { dialect: null, ...samplingOf(tuning) };
}

export function encodeAnthropicLegacyTuning(tuning: ResolvedTuning): AnthropicLegacyTuningParams {
  const dialect = "anthropic-legacy";
  const { effort } = tuning;
  switch (effort.kind) {
    case "vendor-default":
      return { dialect, ...samplingOf(tuning) };
    case "off":
      offFormIn(OFF_FORMS_BY_DIALECT[dialect], effort.form, dialect);
      return { dialect, ...samplingOf(tuning) };
    case "level": {
      const budgetTokens =
        ANTHROPIC_LEGACY_BUDGET_TOKENS[wordIn(ANTHROPIC_LEGACY_LEVELS, effort.rung, dialect)];
      return {
        dialect,
        thinking: {
          type: "enabled",
          budget_tokens: budgetTokens,
          ...(tuning.display !== undefined && { display: tuning.display })
        },
        maxTokensFloor: budgetTokens + 1,
        ...samplingOf(tuning)
      };
    }
  }
}

const ADAPTIVE_OFF_THINKING = {
  omit: undefined,
  disabled: { type: "disabled" },
  "between-tools": { type: "between_tools" }
} as const satisfies Record<
  (typeof OFF_FORMS_BY_DIALECT)["anthropic-adaptive"][number],
  AnthropicAdaptiveThinkingField | undefined
>;

export function encodeAnthropicAdaptiveTuning(
  tuning: ResolvedTuning
): AnthropicAdaptiveTuningParams {
  const dialect = "anthropic-adaptive";
  const { effort } = tuning;
  switch (effort.kind) {
    case "vendor-default":
      return { dialect, ...samplingOf(tuning) };
    case "off": {
      const thinking =
        ADAPTIVE_OFF_THINKING[offFormIn(OFF_FORMS_BY_DIALECT[dialect], effort.form, dialect)];
      return { dialect, ...(thinking && { thinking }), ...samplingOf(tuning) };
    }
    case "level":
      return {
        dialect,
        thinking: { type: "adaptive", display: tuning.display ?? "summarized" },
        outputConfig: { effort: wordIn(ANTHROPIC_ADAPTIVE_LEVELS, effort.rung, dialect) },
        ...samplingOf(tuning)
      };
  }
}

export function encodeOpenAIReasoningTuning(tuning: ResolvedTuning): OpenAIReasoningTuningParams {
  const dialect = "openai-reasoning";
  const { effort } = tuning;
  switch (effort.kind) {
    case "vendor-default":
      return { dialect, ...samplingOf(tuning) };
    case "off":
      offFormIn(OFF_FORMS_BY_DIALECT[dialect], effort.form, dialect);
      return { dialect, reasoningEffort: "none", ...samplingOf(tuning) };
    case "level":
      return {
        dialect,
        reasoningEffort: wordIn(OPENAI_EFFORT_LEVELS, effort.rung, dialect),
        ...samplingOf(tuning)
      };
  }
}

export function encodeGeminiBudgetTuning(tuning: ResolvedTuning): GeminiBudgetTuningParams {
  const dialect = "gemini-budget";
  const { effort } = tuning;
  switch (effort.kind) {
    case "vendor-default":
      return { dialect, ...samplingOf(tuning) };
    case "off":
      offFormIn<EffortOffForm>(OFF_FORMS_BY_DIALECT[dialect], effort.form, dialect);
      return { dialect, ...samplingOf(tuning) };
    case "level":
      return {
        dialect,
        thinkingConfig: {
          thinkingBudget: GEMINI_BUDGET_TOKENS[wordIn(GEMINI_BUDGET_LEVELS, effort.rung, dialect)]
        },
        ...samplingOf(tuning)
      };
  }
}

const GEMINI_WIRE_LEVELS = {
  minimal: "MINIMAL",
  low: "LOW",
  medium: "MEDIUM",
  high: "HIGH"
} as const satisfies { [Level in GeminiLevelLevel]: Uppercase<Level> };

const toGeminiWireLevel = (level: GeminiLevelLevel): GeminiWireLevel => GEMINI_WIRE_LEVELS[level];

export function encodeGeminiLevelTuning(tuning: ResolvedTuning): GeminiLevelTuningParams {
  const dialect = "gemini-level";
  const { effort } = tuning;
  switch (effort.kind) {
    case "vendor-default":
      return { dialect, ...samplingOf(tuning) };
    case "off":
      return {
        dialect,
        thinkingConfig: {
          thinkingLevel: toGeminiWireLevel(
            offFormIn(OFF_FORMS_BY_DIALECT[dialect], effort.form, dialect)
          ),
          includeThoughts: false
        },
        ...samplingOf(tuning)
      };
    case "level":
      return {
        dialect,
        thinkingConfig: {
          thinkingLevel: toGeminiWireLevel(wordIn(GEMINI_LEVEL_LEVELS, effort.rung, dialect)),
          includeThoughts: true
        },
        ...samplingOf(tuning)
      };
  }
}

export function encodeKimiEffortTuning(tuning: ResolvedTuning): KimiEffortTuningParams {
  const dialect = "kimi-effort";
  const { effort } = tuning;
  switch (effort.kind) {
    case "vendor-default":
      return { dialect, ...samplingOf(tuning) };
    case "off":
      offFormIn(OFF_FORMS_BY_DIALECT[dialect], effort.form, dialect);
      return { dialect, thinking: { type: "disabled" }, ...samplingOf(tuning) };
    case "level":
      return {
        dialect,
        reasoningEffort: wordIn(KIMI_EFFORT_LEVELS, effort.rung, dialect),
        ...samplingOf(tuning)
      };
  }
}
